import React, { useState, useRef } from "react";

import { Divider } from "Components/atoms/Divider";
import { FlightPriceMobile } from "Components/molecules/FlightPrice/Mobile";
import { FlightSector } from "Components/molecules/FlightSector/Mobile";
import { ExpandedFlightCard } from "Components/organisms/ExpandedFlightCard/mobile";
import { ExpandedFlightButton } from "Components/organisms/ExpandedFlightButton";

import {
  FlightCardContainer,
  SegmentsContainer,
  FlightCardWrapper,
  PriceContainer,
} from "./styles";

import { FlightCardProps } from "./types";
import { sendGTMEvent } from "shared/services/GTM";
import { convertDateToDDMMAAAA } from "shared/utils/dates";
import { extractAirlines } from "shared/utils/extractAirlines";
import { FlightDetailsModal } from "Components/molecules/FlightDetailsModal";
import { getDeeplink } from "shared/utils/DeepLink/getDeepLink";

/**
 * Componente FlightCard
 *
 * Exibe um cartão com informações sobre um voo (ida e volta, preço), com opção de expansão para mais detalhes.
 *
 * @param {SERPCardResult} cardResult - Informações sobre o Card de Resultados
 */
export const FlightCardMobile: React.FC<FlightCardProps> = ({
  cardResult,
  resultPosition = { indexPosition: 1, currentPage: 1 },
}) => {
  const [isVisibleFlightDetailsModal, setVisibleFlightDetailsModal] =
    useState(false);
  const [detailsMode, setDetailsMode] = useState<"details" | "sellers">(
    "details",
  );

  const cardRef = useRef<HTMLDivElement>(null);

  /**
   * GTM
   */
  const handleTriggerEvent = () => {
    sendGTMEvent("flightCard", {
      resultsPosition: resultPosition?.indexPosition + 1,
      page: resultPosition?.currentPage,
      airlines: extractAirlines(cardResult.sectors),
      minPrice: cardResult.minPrice,
      IATA_origin: cardResult.sectors[0].departureAirportIata,
      IATA_destination: cardResult.sectors[0].arrivalAirportIata,
      departure_date: convertDateToDDMMAAAA(
        cardResult.sectors[0].departureDate,
      ),
      return_date: cardResult.sectors[1]
        ? convertDateToDDMMAAAA(cardResult.sectors[1].departureDate)
        : "",
    });
  };

  /**
   * Abre o seller com o menor preço disponível no card.
   */
  const handleOpenFirstSeller = () => {
    const cheapestSeller = cardResult?.sellers?.reduce((cheapest, seller) =>
      seller.price < cheapest.price ? seller : cheapest,
    );
    if (!cheapestSeller) return;

    handleTriggerEvent();

    window.open(
      getDeeplink(cardResult, cheapestSeller),
      "_blank",
      "noopener,noreferrer",
    );
  };

  /**
   * Abre modal
   */
  const handleExpand = (mode: "details" | "sellers") => {
    setDetailsMode(mode);
    setVisibleFlightDetailsModal(true);
  };

  return (
    <FlightCardWrapper ref={cardRef}>
      <FlightCardContainer onClick={handleOpenFirstSeller}>
        <SegmentsContainer>
          <FlightSector {...cardResult?.sectors[0]} />

          {cardResult?.sectors[1] && (
            <>
              <Divider styleType="dashed" />
              <FlightSector {...cardResult?.sectors[1]} />
            </>
          )}
        </SegmentsContainer>

        <PriceContainer>
          <FlightPriceMobile
            price={cardResult?.minPrice}
            currency={"R$"}
            onButtonClick={() => handleExpand("details")}
          />
        </PriceContainer>
      </FlightCardContainer>

      <ExpandedFlightButton
        sellersCount={cardResult.sellers.length}
        onClick={() => handleExpand("sellers")}
      />

      {isVisibleFlightDetailsModal && (
        <FlightDetailsModal
          isVisibleModal={true}
          onClose={() => setVisibleFlightDetailsModal(false)}
        >
          <ExpandedFlightCard
            cardResult={cardResult}
            mode={detailsMode}
            onClose={() => setVisibleFlightDetailsModal(false)}
            onWhereToBuy={() => {
              if (detailsMode === "details") {
                setDetailsMode("sellers");
                return;
              }

              handleOpenFirstSeller();
            }}
          />
        </FlightDetailsModal>
      )}
    </FlightCardWrapper>
  );
};
